import React from "react";
import axios from "axios";
import { API_autoschedule_settings } from "../../../../../constants";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";
import {
  DEFAULT_ROLE_MATCHING_STRENGTH, DEFAULT_OPENING_CLOSING_STRENGTH,
  DEFAULT_SEARCH_EFFORT, ROLE_TARGET_WEIGHT_SCALE,
  roleMatchingStrengthToWeight, openingClosingStrengthToWeight,
} from "./AutoScheduleOptimizationSettings";

export const defaultDraft = {
  roleMatchingStrength: DEFAULT_ROLE_MATCHING_STRENGTH, openingClosingStrength: DEFAULT_OPENING_CLOSING_STRENGTH,
  searchEffort: DEFAULT_SEARCH_EFFORT, allowOpenShifts: false, enforceOpeningClosingStaffing: false,
};

export function draftFromOptions(options) {
  return {
    roleMatchingStrength: options.role_target_fit_weight / ROLE_TARGET_WEIGHT_SCALE,
    openingClosingStrength: (options.opening_closing_consistency_weight ?? 0.5) * 100,
    searchEffort: options.search_effort,
    allowOpenShifts: options.allow_open_shifts,
    enforceOpeningClosingStaffing: options.enforce_opening_closing_staffing ?? false,
  };
}

export function optionsFromDraft(draft) {
  return {
    role_target_fit_weight: roleMatchingStrengthToWeight(draft.roleMatchingStrength),
    opening_closing_consistency_weight: openingClosingStrengthToWeight(draft.openingClosingStrength),
    search_effort: draft.searchEffort,
    allow_open_shifts: draft.allowOpenShifts,
    enforce_opening_closing_staffing: draft.enforceOpeningClosingStaffing ?? false,
  };
}

export default function useAutoscheduleStoreDefaults(storeId) {
  const [state, setState] = React.useState({ storeId: null, draft: defaultDraft, saved: defaultDraft, ready: false });
  const [retry, setRetry] = React.useState(0);
  const generation = React.useRef(0);
  const activeStore = React.useRef(storeId);
  activeStore.current = storeId;
  React.useEffect(() => {
    let active = true;
    generation.current += 1;
    setState({ storeId, draft: defaultDraft, saved: defaultDraft, ready: !storeId, error: "", saving: false });
    if (storeId) {
      axios.get(API_autoschedule_settings + storeId, getDefaultAxiosConfig()).then(({ data }) => {
        if (!active) return;
        const draft = draftFromOptions(data.effective_defaults);
        setState({ storeId, draft, saved: draft, ready: true, error: "", saving: false });
      }).catch(() => {
        if (active) setState((previous) => ({ ...previous, error: "Could not load this store's settings. Retry before scheduling." }));
      });
    }
    return () => { active = false; generation.current += 1; };
  }, [storeId, retry]);
  const ready = state.storeId === storeId && state.ready;
  const draft = state.storeId === storeId ? state.draft : defaultDraft;
  const setOption = (key, value) => setState((previous) => ({ ...previous, draft: { ...previous.draft, [key]: value }, notice: "" }));
  const save = async () => {
    if (!ready || state.saving || !storeId) return;
    const submitted = { ...draft };
    const saveGeneration = generation.current;
    setState((previous) => ({ ...previous, saving: true, error: "", notice: "" }));
    try {
      const { data } = await axios.put(API_autoschedule_settings + storeId, { defaults: optionsFromDraft(submitted) }, getDefaultAxiosConfig());
      if (activeStore.current !== storeId || generation.current !== saveGeneration) return;
      const saved = draftFromOptions(data.effective_defaults);
      setState((previous) => ({ ...previous, saved, saving: false, notice: "Store defaults saved." }));
    } catch {
      if (activeStore.current === storeId && generation.current === saveGeneration) setState((previous) => ({ ...previous, saving: false, error: "Could not save store defaults. Your changes are still here." }));
    }
  };
  return {
    draft, ready, saving: state.saving, error: state.storeId === storeId ? state.error : "",
    notice: state.storeId === storeId ? state.notice : "", setOption, save,
    reload: () => setRetry((value) => value + 1),
    reset: () => setState((previous) => ({ ...previous, draft: { ...previous.saved }, notice: "" })),
  };
}
